import { HTMLAttributes } from 'react';
import { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { ModularTableProps, RowType, SelectOption } from '@tryhackme/thm-ui-components';

export interface CompanyParams {
  companyId: string;
}

export interface CompanySelectOption {
  id: string;
  value: string;
  label: string;
}

export interface CorporateParams {
  companyId: string;
  page: number;
  limit: number;
  search: string;
  sort: string;
}

export interface AllCompaniesItem {
  id: string;
  value: string;
  label: string;
}

export interface AllCompaniesData {
  totalDocs: string;
  companies: AllCompaniesItem[];
}

export interface CompanyItem {
  _id: string;
  name: string;
  logo: string;
  url?: string;
  type: string;
  apiInfo?: { active: boolean };
  features?: {
    paths: boolean;
    socSim: boolean;
    sso?: { enabled: false } | { enabled: true; workOSOrganizationId: string; createdAt: string };
  };
  subscription?: { chargebee?: { customerId?: string; cancellationScheduled?: boolean } };
  totalUsersCount?: string;
  deadline: string;
  isExpired: boolean;
  isTrial: boolean;
  tokens: { amount: number };
  additionalContent?: {
    azureTenantLabs?: {
      labsReady?: boolean;
      tenantId?: string;
      primaryDomain?: string;
    };
  };
  companyUsers?: CompanySubscriberData;
  companyAdmins?: CompanyInstructorData;
}

export interface CompanyData {
  totalDocs: string;
  companyDetails: CompanyItem;
}

export interface AddSeatModalProps {
  companyDetails: CompanyItem;
}
export interface CompanyInstructorItems extends RowType {
  email: string;
  username: string;
  avatar: string;
}

export interface CompanyInstructorData {
  totalDocs: string;
  users: CompanyInstructorItems[];
}

export interface CompanySubscriberItems extends RowType {
  seatId?: string;
  subActivated: boolean;
  email: string;
  subExpiry: string;
  added: string;
}

export interface CompanySubscriberData {
  totalDocs: string;
  users: CompanySubscriberItems[];
}

export type UserTableType<T> = ModularTableProps<T & RowType>;

export interface CorporateProps<T extends CompanySubscriberItems | CompanyInstructorItems> {
  companyDetails: CompanyItem;
  title: string;
  data?: T extends CompanySubscriberItems ? CompanySubscriberData : CompanyInstructorData;
  setFeedbackParams: SetFeedbackParamsFunction;
  feedbackParams: CorporateParams;
  columns: UserTableType<T>['columns'];
  icon: IconDefinition;
  companyList?: SelectOption[];
  onCompanyChange?: (x: string) => void;
  isSubscriber?: T extends CompanySubscriberItems ? true : false;
  companyTrial?: boolean;
  totalUsersCount?: string;
}

export interface BaseModalProps {
  companyId: string;
  icon: IconDefinition;
}

export interface UpdateTokenModalProps extends BaseModalProps {
  title: string;
  numberOfTokens: number;
  buttonTitle: string;
  isAddToken?: boolean;
}

export interface SidePanelSelfServeRowProps {
  label: string;
  selfServeBilling?: string;
  selfServeRenewal: boolean;
}

export enum AllowedToggleModes {
  PATHS = 'PATHS',
  API_KEYS = 'API_KEYS',
  SOC_SIM = 'SOC_SIM',
}

export interface ToggleRowProps extends HTMLAttributes<HTMLLabelElement> {
  mode: AllowedToggleModes;
  companyId: string;
  toggleStatus: boolean;
}

export interface CreateCompanyInterface {
  name: string;
  url: string;
  deadline?: string;
  isTrial: boolean;
  type: string;
}

export interface CreateCompanyData extends CreateCompanyInterface {
  adminEmails: string[];
}

export interface CreateCompanyProps extends CreateCompanyInterface {
  emails: string;
}

export interface CreateSeatInterface {
  companyId: string;
  seatType: string;
  subDuration: string;
}

export interface CreateSeatData extends CreateSeatInterface {
  userEmails: string[];
  blankUsers: number;
}

export interface CreateSeatProps extends CreateSeatInterface {
  emails: string;
  blankUsers: string;
}

export interface CreateInstructorData {
  companyId: string;
  adminEmails: string[];
}

export interface CreateInstructorProps {
  companyId: string;
  emails: string;
}

export interface DeleteSeatData {
  companyId: string;
  seatId: string;
}

export interface DeleteAdminData {
  companyId: string;
  identifier: string;
}

export interface ToggleData {
  companyId: string;
  status: string;
}

export interface UpdateTokenData {
  companyId: string;
  tokens: number;
  operation: 'increment' | 'decrement';
}

export interface UpdateTokenProps {
  companyId: string;
  tokens: string;
}

export interface UpdateDeadlineData {
  companyId: string;
  deadline: string;
  isTrial: boolean;
}

export interface UpdateRenewDashboardData {
  companyId: string;
  renewalDate: string;
}

export interface EnableSSOData {
  companyId: string;
}

export interface SidePanelProps {
  companyId: string;
  companyLogo: string;
  companyWebsite?: string;
  companyName: string;
  companyType: string;
  username: string;
  expiryDate?: string;
  selfServeBilling?: string;
  selfServeRenewal?: boolean;
  pathActive: boolean;
  apiKeys: boolean;
  tokens: number;
  ssoEnabled: boolean;
  socSimEnabled: boolean;
}

export interface InstructorModalProps {
  width: string;
  companyId: string;
}

export interface InstructorProps {
  companyDetails: CompanyItem;
  instructorData: CompanyInstructorData;
  setFeedbackParams: SetFeedbackParamsFunction;
  feedbackParams: CorporateParams;
}

export interface CorporateContainerProps {
  companiesData: CompanySelectOption[];
}

export interface CompanyBarProps {
  options: CompanySelectOption[];
  selected: CompanySelectOption;
  onChange: (data: CompanySelectOption) => void;
}

export interface CreateCompanyModalProps {
  onChange: (data: CompanySelectOption) => void;
}

export interface SubscribersProps {
  companyDetails: CompanyItem;
  subscriberData: CompanySubscriberData;
  setFeedbackParams: SetFeedbackParamsFunction;
  isSubscriber?: boolean;
  feedbackParams: CorporateParams;
  companyTrial: boolean;
  totalUsersCount: string;
}

export interface AddCompanyResponseData {
  id: string;
  name: string;
}

export type SetActiveSortColumnFunction<T> = React.Dispatch<React.SetStateAction<UserTableType<T>['activeSortColumn']>>;

export type SetFeedbackParamsFunction = React.Dispatch<React.SetStateAction<CorporateParams>>;

export interface UpdateCompanyDetailsModalProps extends BaseModalProps {
  title: string;
  buttonTitle: string;
  updateButtonText: string;
  defaultValues: Pick<UpdateCompanyDetailsProps, 'companyName' | 'url' | 'type'>;
  logo: string;
}

export interface UpdateCompanyDetailsProps {
  companyId: string;
  companyName: string;
  url?: string;
  filename?: string;
  type: string;
}

export interface GetPreSignedUrlResponse {
  url: string;
  filename: string;
}

export interface GetPreSignedUrlBody {
  companyId: string;
  filename: string;
}

export interface DeleteUserModalProps {
  isModalOpen: boolean;
  isLoading: boolean;
  title: string;
  description: string;
  deleteButtonText: string;
  onModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteUser: () => Promise<void>;
}
